/**
 * Visit location trail — the nurse's own record of where she was during a visit.
 *
 * Starts at clock-in, takes a reading every few minutes, notes when the app
 * goes away and comes back, and stops at clock-out or after a hard time cap.
 *
 * ─── This is not background location ───
 *
 * Nothing here runs while the app is closed, and nothing here asks for the
 * "Always" permission. iOS and Android suspend our JS the moment the app
 * leaves the screen, so the interval simply stops; the app-lifecycle events are
 * what make that gap visible instead of silent, and the catch-up reading on
 * return is what keeps the trail useful for a nurse who pockets her phone. The
 * honest description is "a reading whenever the app is actually open", not "a
 * reading every three minutes".
 *
 * That also means this ships in an OTA update: it is ordinary JS on top of
 * expo-location, which the clock-in flow already uses under a permission the
 * nurse has already granted.
 *
 * ─── Never a gate ───
 *
 * Every entry point swallows its own errors. A failed reading, a failed flush
 * or a revoked permission must never surface to the nurse or block a clock-in,
 * a save or a clock-out. The trail is evidence; the visit is the job.
 *
 * ─── Where the timer lives ───
 *
 * In app/_layout.tsx, not on the visit screen. The recorder heartbeat learned
 * this the hard way: pinning an interval to one screen means it dies the
 * moment the nurse navigates to the assessment, which is where she spends the
 * visit. See the comment on Recorder's heartbeat in that file.
 */
import Constants from "expo-constants";

import { postVisitActivity, type VisitActivityEvent } from "@/lib/api";
import {
    captureLocation,
    PING_SETTLE_MS,
    PING_TARGET_ACCURACY_M,
    type LocationBlockReason
} from "@/lib/capture-location";

/** How often we want a reading while the app is open. */
export const PING_INTERVAL_MS = 3 * 60_000;

/**
 * Hard stop, measured from clock-in. A visit that is still "in progress" three
 * hours later is a nurse who forgot to clock out, and following her phone for
 * the rest of the day is neither useful evidence nor ours to collect.
 */
export const MAX_TRACKING_MS = 3 * 60 * 60_000;

/**
 * Flush cadence in the good case is "after each reading", so a working phone
 * sends one small request every 3 minutes. Batching is what happens when that
 * fails: the queue is kept and retried on the next tick, which is exactly the
 * case that matters, because nurses work inside homes with no signal.
 */
const MAX_QUEUED_EVENTS = 200;

interface TrailState {
    visitId: number | null;
    startedAt: number | null;
    lastPingAt: number | null;
}

let state: TrailState = { visitId: null, startedAt: null, lastPingAt: null };
let queue: VisitActivityEvent[] = [];
let flushing = false;

function appVersion(): string {
    return Constants.expoConfig?.version ?? "unknown";
}

function enqueue(event: VisitActivityEvent): void {
    queue.push(event);
    // A phone that has been offline for hours must not grow an unbounded queue.
    // The oldest readings are the least interesting when we have to choose.
    if (queue.length > MAX_QUEUED_EVENTS) {
        queue = queue.slice(queue.length - MAX_QUEUED_EVENTS);
    }
}

/** True while a visit is being tracked and the time cap has not been reached. */
export function isTracking(now: number = Date.now()): boolean {
    if (state.visitId == null || state.startedAt == null) {
        return false;
    }
    return now - state.startedAt < MAX_TRACKING_MS;
}

/**
 * Does this tick owe us a reading? Pure so the interval logic can be tested
 * without a device.
 */
export function isPingDue(
    { startedAt, lastPingAt }: { startedAt: number | null; lastPingAt: number | null },
    now: number
): boolean {
    if (startedAt == null) {
        return false;
    }
    if (now - startedAt >= MAX_TRACKING_MS) {
        return false;
    }
    if (lastPingAt == null) {
        return true;
    }
    return now - lastPingAt >= PING_INTERVAL_MS;
}

async function flush(): Promise<void> {
    const visitId = state.visitId;
    if (visitId == null || queue.length === 0 || flushing) {
        return;
    }
    flushing = true;
    // Take the batch out of the queue, but put it back on failure: a dropped
    // batch is a hole in the trail, and the server dedupes retries anyway.
    const batch = queue;
    queue = [];
    try {
        await postVisitActivity(visitId, { appVersion: appVersion(), events: batch });
    } catch {
        queue = [...batch, ...queue];
        if (queue.length > MAX_QUEUED_EVENTS) {
            queue = queue.slice(queue.length - MAX_QUEUED_EVENTS);
        }
    } finally {
        flushing = false;
    }
}

async function takeReading(trigger: "clock_in" | "interval" | "foreground"): Promise<void> {
    const now = Date.now();
    state.lastPingAt = now;
    const capture = await captureLocation({
        settleMs: PING_SETTLE_MS,
        targetAccuracyM: PING_TARGET_ACCURACY_M
    });

    if (capture.location) {
        enqueue({
            kind: "gps_ping",
            occurredAt: new Date(now).toISOString(),
            lat: capture.location.lat,
            lng: capture.location.lng,
            accuracy: capture.location.accuracy,
            trigger,
            quality: capture.location.quality
        });
    } else {
        // A reading that could not be taken is evidence too: it is how ops
        // separates "no signal in the building" from "she closed the app".
        enqueue({
            kind: "gps_ping",
            occurredAt: new Date(now).toISOString(),
            trigger,
            blockReason: capture.reason satisfies LocationBlockReason
        });
    }
    await flush();
}

export const VisitLocationTrail = {
    /**
     * Begin tracking. Idempotent for the same visit, so the auto-resume effect
     * on the visit screen can call it on every mount without restarting the
     * clock or double-sampling.
     */
    start(visitId: number): void {
        if (state.visitId === visitId) {
            return;
        }
        state = { visitId, startedAt: Date.now(), lastPingAt: null };
        queue = [];
        void takeReading("clock_in").catch(() => {
            /* never surfaces to the nurse */
        });
    },

    /** The visit being tracked, or null. */
    trackedVisitId(): number | null {
        return state.visitId;
    },

    /** Stop and send whatever is left. Safe to call when not tracking. */
    stop(): void {
        void VisitLocationTrail.stopAndFlush();
    },

    /**
     * `stop`, resolving once the leftover points are sent. Used before clocking
     * in to the next visit, so that visit's tracking cannot start while this one
     * is still flushing. The reset only clears the visit it stopped: if tracking
     * moved to another visit meanwhile, that one is left alone.
     */
    async stopAndFlush(): Promise<void> {
        const stopping = state.visitId;
        if (stopping == null) {
            return;
        }
        try {
            await flush();
        } catch {
            /* best effort */
        } finally {
            if (state.visitId === stopping) {
                state = { visitId: null, startedAt: null, lastPingAt: null };
                queue = [];
            }
        }
    },

    /**
     * Called by the global interval in app/_layout.tsx. Ticks more often than
     * the ping interval on purpose: JS timers are throttled and suspended at
     * the OS's discretion, so "has enough time passed" is the reliable question,
     * not "did a 3-minute timer fire".
     */
    async tick(isForeground: boolean): Promise<void> {
        try {
            if (!isForeground || !isTracking()) {
                return;
            }
            if (isPingDue(state, Date.now())) {
                await takeReading("interval");
            } else {
                // Nothing due, but a previous flush may still be owed.
                await flush();
            }
        } catch {
            /* never surfaces to the nurse */
        }
    },

    /**
     * Called from the AppState listener. On the way out we record the moment
     * and try one flush (it may not finish before the OS suspends us, which is
     * why the queue survives). On the way back we record the return, and take a
     * catch-up reading when the last one has gone stale.
     */
    async onAppStateChange(next: "active" | "background"): Promise<void> {
        try {
            if (state.visitId == null) {
                return;
            }
            enqueue({
                kind: next === "active" ? "app_foreground" : "app_background",
                occurredAt: new Date().toISOString()
            });
            if (next === "background") {
                await flush();
                return;
            }
            if (isTracking() && isPingDue(state, Date.now())) {
                await takeReading("foreground");
            } else {
                await flush();
            }
        } catch {
            /* never surfaces to the nurse */
        }
    },

    /** Test seam. */
    _state(): TrailState {
        return { ...state };
    },
    _reset(): void {
        state = { visitId: null, startedAt: null, lastPingAt: null };
        queue = [];
        flushing = false;
    }
};
